import { useState, useId } from 'react'
import { Calendar } from '../../../../components/ui/calendar'
import { useDropdown } from '../../../../shared/utils/DropdownContext'
import { DATE_PRESET_OPTIONS, type DatePreset, type DateMode } from '../../types/filters.types'

interface DateFilterProps {
  dateMode: DateMode
  datePreset?: DatePreset
  dateRange?: { start: Date; end: Date }
  selectedDates: Date[]
  onDateModeChange: (mode: DateMode) => void
  onDatePresetChange: (preset: DatePreset) => void
  onDateRangeChange: (range: { start: Date; end: Date }) => void
  onSelectedDatesChange: (dates: Date[]) => void
  className?: string
}

export const DateFilter = ({
  dateMode,
  datePreset,
  dateRange,
  selectedDates,
  onDateModeChange,
  onDatePresetChange,
  onDateRangeChange,
  onSelectedDatesChange,
  className = ''
}: DateFilterProps) => {
  const dropdownId = useId()
  const { openDropdown, setOpenDropdown } = useDropdown()
  const showDropdown = openDropdown === dropdownId
  
  const [showCalendar, setShowCalendar] = useState(false)
  const [startDate, setStartDate] = useState<Date | null>(dateRange?.start || null)
  const [endDate, setEndDate] = useState<Date | null>(dateRange?.end || null)

  const getDisplayText = () => {
    if (dateMode === 'preset' && datePreset) {
      const option = DATE_PRESET_OPTIONS.find(opt => opt.value === datePreset)
      return option?.title || 'Date'
    }
    if (dateMode === 'between' && dateRange) {
      return 'Between dates'
    }
    if (selectedDates.length > 0) {
      return `${selectedDates.length} date${selectedDates.length > 1 ? 's' : ''} selected`
    }
    return 'Date'
  }

  const isActive = dateMode !== 'none' || selectedDates.length > 0

  const toggleDropdown = () => {
    setOpenDropdown(showDropdown ? null : dropdownId)
    setShowCalendar(false)
  }

  const handlePresetSelect = (preset: string) => {
    if (preset === 'between') {
      onDateModeChange('between')
      setShowCalendar(true)
    } else if (preset === 'Date') {
      onDateModeChange('none')
      onSelectedDatesChange([])
      setOpenDropdown(null)
    } else {
      onDateModeChange('preset')
      onDatePresetChange(preset as DatePreset)
      
      // Calculate date range for preset
      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      
      switch (preset) {
        case 'past24h': {
          const yesterday = new Date(today)
          yesterday.setDate(yesterday.getDate() - 1)
          onDateRangeChange({ start: yesterday, end: today })
          break
        }
        case 'week': {
          const weekAgo = new Date(today)
          weekAgo.setDate(weekAgo.getDate() - 7)
          onDateRangeChange({ start: weekAgo, end: today })
          break
        }
        case 'month': {
          const monthAgo = new Date(today)
          monthAgo.setMonth(monthAgo.getMonth() - 1)
          onDateRangeChange({ start: monthAgo, end: today })
          break
        }
        case 'year': {
          const yearAgo = new Date(today)
          yearAgo.setFullYear(yearAgo.getFullYear() - 1)
          onDateRangeChange({ start: yearAgo, end: today })
          break
        }
      }
      setOpenDropdown(null)
    }
  }

  const handleDateRangeApply = () => {
    if (startDate && endDate) {
      onDateRangeChange({ start: startDate, end: endDate })
      setOpenDropdown(null)
      setShowCalendar(false)
    }
  }

  const handleDateRangeClear = () => {
    setStartDate(null)
    setEndDate(null)
    onDateModeChange('none')
    onDateRangeChange({ start: new Date(), end: new Date() })
  }

  return (
    <div className={`relative ${className}`}>
      <div
        className={`w-[166px] h-[40px] rounded-[10px] pt-[12px] pr-[10px] pb-[12px] pl-[10px] flex items-center justify-between cursor-pointer ${
          isActive ? '' : 'bg-muted'
        } ${isActive ? 'hover:opacity-90' : 'hover:bg-muted'}`}
        style={isActive ? {
          background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))'
        } : {}}
        onClick={toggleDropdown}
      >
        <span
          className={`font-semibold text-[16px] ${isActive ? 'text-[#FBFBFE]' : 'text-foreground'}`}
          style={!isActive ? {
            lineHeight: '140%',
            letterSpacing: '0%',
            background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          } : {
            lineHeight: '140%',
            letterSpacing: '0%'
          }}
        >
          {getDisplayText()}
        </span>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={`transform transition-transform ${showDropdown ? 'rotate-180' : ''}`}
        >
          <path
            d="M5 7.5L10 12.5L15 7.5"
            stroke={isActive ? "#FBFBFE" : "#3F3E42"}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {showDropdown && !showCalendar && (
        <div
          className="absolute top-full left-0 right-0 mt-2 bg-card rounded-[10px] p-3 z-10 min-w-[200px]"
          style={{ boxShadow: "0px 13px 15px 5px #0302084D" }}
        >
          {DATE_PRESET_OPTIONS.map((option) => (
            <div
              key={option.value}
              className="flex justify-between items-center p-2 hover:bg-muted rounded cursor-pointer"
              onClick={() => handlePresetSelect(option.value)}
            >
              <span className="text-foreground font-[400] text-[16px]">
                {option.title}
              </span>
              {((dateMode === 'preset' && datePreset === option.value) || 
                (dateMode === 'between' && option.value === 'between') ||
                (dateMode === 'none' && option.value === 'Date')) && (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.3307 4L5.9974 11.3333L2.66406 8"
                    stroke="#3F3E42"
                    strokeWidth="1.33333"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
          ))}
        </div>
      )}

      {showDropdown && showCalendar && (
        <div
          className="absolute top-full left-0 mt-2 bg-card rounded-[10px] p-4 z-10 min-w-[400px]"
          style={{ boxShadow: "0px 13px 15px 5px #0302084D" }}
        >
          <div className="mb-4">
            <h3 className="font-semibold text-[16px] text-foreground mb-3">Between dates</h3>
            
            <div className="flex gap-2 items-center mb-4">
              <div className="flex-1">
                <label className="block text-sm text-foreground font-semibold mb-1">Start</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="dd/mm/yyyy"
                    value={startDate ? startDate.toLocaleDateString('en-GB') : ''}
                    onChange={(e) => {
                      const parts = e.target.value.split('/')
                      if (parts.length === 3) {
                        const day = parseInt(parts[0])
                        const month = parseInt(parts[1]) - 1
                        const year = parseInt(parts[2])
                        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
                          setStartDate(new Date(year, month, day))
                        }
                      } else if (e.target.value === '') {
                        setStartDate(null)
                      }
                    }}
                    className="w-full p-3 pr-10 border border-border rounded-md text-foreground focus:border-[#3F3E42] focus:outline-none"
                  />
                  <svg
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path d="M13.3333 2.66667H2.66667C1.93029 2.66667 1.33333 3.26362 1.33333 4V13.3333C1.33333 14.0697 1.93029 14.6667 2.66667 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V4C14.6667 3.26362 14.0697 2.66667 13.3333 2.66667Z" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10.6667 1.33333V4" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M5.33333 1.33333V4" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M1.33333 6.66667H14.6667" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
              <span className="text-foreground mt-6 font-semibold">-</span>
              <div className="flex-1">
                <label className="block text-sm text-foreground font-semibold mb-1">End</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="dd/mm/yyyy"
                    value={endDate ? endDate.toLocaleDateString('en-GB') : ''}
                    onChange={(e) => {
                      const parts = e.target.value.split('/')
                      if (parts.length === 3) {
                        const day = parseInt(parts[0])
                        const month = parseInt(parts[1]) - 1
                        const year = parseInt(parts[2])
                        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
                          setEndDate(new Date(year, month, day))
                        }
                      } else if (e.target.value === '') {
                        setEndDate(null)
                      }
                    }}
                    className="w-full p-3 pr-10 border border-border rounded-md text-foreground focus:border-[#3F3E42] focus:outline-none"
                  />
                  <svg
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path d="M13.3333 2.66667H2.66667C1.93029 2.66667 1.33333 3.26362 1.33333 4V13.3333C1.33333 14.0697 1.93029 14.6667 2.66667 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V4C14.6667 3.26362 14.0697 2.66667 13.3333 2.66667Z" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10.6667 1.33333V4" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M5.33333 1.33333V4" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M1.33333 6.66667H14.6667" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <Calendar 
            className="w-full"
            mode="single"
            onSelect={(date) => {
              if (date) {
                if (!startDate) {
                  // First click - set start date
                  setStartDate(date)
                } else if (!endDate) {
                  // Second click - set end date
                  if (date >= startDate) {
                    setEndDate(date)
                  } else {
                    // If second date is before first, swap them
                    setEndDate(startDate)
                    setStartDate(date)
                  }
                } else {
                  // Both dates already selected - start over with new start date
                  setStartDate(date)
                  setEndDate(null)
                }
              }
            }}
            selected={startDate || endDate || undefined}
          />
          
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground text-center">
              You can choose multiple date
            </p>
            
            <div className="flex gap-2">
              <button
                onClick={handleDateRangeClear}
                className="flex-1 px-6 py-3 border border-border rounded-md text-foreground font-medium hover:bg-muted transition-colors"
              >
                Clear
              </button>
              <button
                onClick={handleDateRangeApply}
                disabled={!startDate || !endDate}
                className="flex-1 px-6 py-3 bg-[#3F3E42] text-white rounded-md font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Apply Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}